import { requestNetworkPanelContext } from 'dashboard/api/networkPanel';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';

const REQUEST_CONTEXT_EVENT = 'tecnet-network-panel:request-context';
const CONTEXT_EVENT = 'tecnet-network-panel:context';
const CONTEXT_ERROR_EVENT = 'tecnet-network-panel:context-error';
const VISIBILITY_EVENT = 'tecnet-network-panel:visibility';
const MAX_REQUEST_ID_LENGTH = 128;
const UNAUTHORIZED_STATUSES = [401, 403, 404];

export const hasTrustedOrigin = (frame, panelOrigin) => {
  if (!frame || !panelOrigin) return false;

  try {
    return new URL(frame.src).origin === panelOrigin;
  } catch {
    return false;
  }
};

const trustedRequest = (event, frame, panelOrigin) =>
  Boolean(
    hasTrustedOrigin(frame, panelOrigin) &&
      event.origin === panelOrigin &&
      event.source === frame.contentWindow &&
      event.data?.event === REQUEST_CONTEXT_EVENT &&
      typeof event.data.request_id === 'string' &&
      event.data.request_id.length > 0 &&
      event.data.request_id.length <= MAX_REQUEST_ID_LENGTH
  );

const validResponse = data =>
  Boolean(
    data &&
      typeof data.token === 'string' &&
      data.token.length > 0 &&
      typeof data.target_origin === 'string'
  );

export const createNetworkPanelBridge = ({
  getFrame,
  getContext,
  isVisible,
  panelOrigin,
}) => {
  const { run, abort } = useAbortableRequest();
  let generation = 0;
  let disposed = false;

  const isCurrent = ({
    requestGeneration,
    frame,
    frameWindow,
    frameSrc,
    accountId,
    conversationId,
  }) => {
    const currentFrame = getFrame();
    const currentContext = getContext();

    return Boolean(
      !disposed &&
        generation === requestGeneration &&
        isVisible() &&
        currentFrame === frame &&
        currentFrame?.contentWindow === frameWindow &&
        currentFrame?.src === frameSrc &&
        currentContext?.accountId === accountId &&
        currentContext?.conversationId === conversationId
    );
  };

  const handleMessage = async event => {
    const frame = getFrame();
    if (
      disposed ||
      !isVisible() ||
      !trustedRequest(event, frame, panelOrigin)
    ) {
      return;
    }

    const context = getContext();
    const accountId = context?.accountId;
    const conversationId = context?.conversationId;
    if (accountId == null || conversationId == null) return;

    generation += 1;
    const requestGeneration = generation;
    const requestId = event.data.request_id;
    const frameWindow = frame.contentWindow;
    const frameSrc = frame.src;
    const requestState = {
      requestGeneration,
      frame,
      frameWindow,
      frameSrc,
      accountId,
      conversationId,
    };

    try {
      const response = await run(
        signal =>
          requestNetworkPanelContext({ accountId, conversationId, signal }),
        { onAbort: null }
      );
      if (!isCurrent(requestState)) return;

      const data = response?.data;
      if (!validResponse(data)) {
        frameWindow.postMessage(
          {
            event: CONTEXT_ERROR_EVENT,
            request_id: requestId,
            code: 'CONTEXT_UNAVAILABLE',
          },
          panelOrigin
        );
        return;
      }
      if (data.target_origin !== panelOrigin) return;

      frameWindow.postMessage(
        { event: CONTEXT_EVENT, request_id: requestId, token: data.token },
        panelOrigin
      );
    } catch (error) {
      if (!isCurrent(requestState)) return;

      const code = UNAUTHORIZED_STATUSES.includes(error?.response?.status)
        ? 'UNAUTHORIZED'
        : 'CONTEXT_UNAVAILABLE';
      frameWindow.postMessage(
        { event: CONTEXT_ERROR_EVENT, request_id: requestId, code },
        panelOrigin
      );
    }
  };

  const notifyVisibility = () => {
    if (disposed) return;

    const visible = isVisible();
    if (!visible) {
      generation += 1;
      abort();
    }

    const frame = getFrame();
    if (!hasTrustedOrigin(frame, panelOrigin)) return;

    frame.contentWindow.postMessage(
      { event: VISIBILITY_EVENT, visible },
      panelOrigin
    );
  };

  const dispose = () => {
    if (disposed) return;

    disposed = true;
    generation += 1;
    abort();
  };

  return { handleMessage, notifyVisibility, dispose };
};
